/**
 * @file index.tsx
 *
 * @fileoverview Fetches an entity and sends it over to the Form widget to render the full
 * form for that entity.
 */

import React, { useContext } from 'react';
import { EntityDefinition } from '~/jobiqo/models/entity';
import FormWidget from '../Widgets/Form';
import GET_FIELDS from './fields.gql';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import { FormWidgetProps } from '~/jobiqo/components/Form/Widgets/Form';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
export interface EntityFormProps
  extends Omit<React.InputHTMLAttributes<HTMLFormElement>, 'name'>,
    Omit<FormWidgetProps, 'definition'> {
  /**
   * The field types can be either BASE_FIELDS or CONFIG_FIELDS, if empty returns all fields.
   */
  fieldTypes?: string;
  /**
   * If true will show the entity name on the top of the form.
   */
  showLabel?: boolean;
  /**
   * Callback for when submitting the form.
   */
  onSubmit?: (data: {} | React.FormEvent) => void;
  /**
   * Callback for form onBlur.
   */
  onBlur?: () => void;
  /**
   * Callback for the form onFocus event.
   */
  onFocus?: () => void;
  /**
   * The list of errors for the form.
   */
  fieldErrorList?: any[];
}

export const EntityForm = ({
  name,
  bundle,
  onSubmit,
  submitAlwaysEnabled = false,
  onCancel,
  onRemove,
  showLabel,
  entity,
  submitLabel,
  overrides,
  extraValidationSchema,
  fieldGroups,
  renderButtons,
  onBlur,
  onFocus,
  ...props
}: EntityFormProps) => {
  const appContext: AppContextInterface = useContext(AppContext);
  const { loading, error, data } = useQuery(GET_FIELDS, {
    variables: {
      name,
      bundle,
      language: appContext.locale,
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return (
      <CenterBox>
        <FormattedMessage id="form_loading" defaultMessage="Loading form..." />
      </CenterBox>
    );
  }
  if (error) {
    return (
      <MessageAlert type="assertive" alertStyle="danger">
        <FormattedMessage
          id="form_error"
          defaultMessage="Error getting form information."
        />
      </MessageAlert>
    );
  }

  const { definition }: { definition: EntityDefinition } = data;

  return (
    <React.Fragment>
      {showLabel && <h1>{definition.label}</h1>}
      <FormWidget
        key={entity?.id}
        fieldGroups={fieldGroups}
        {...(onSubmit ? { onSubmit: (formData) => onSubmit(formData) } : null)}
        {...(onCancel ? { onCancel } : null)}
        {...(onRemove ? { onRemove } : null)}
        name={name}
        bundle={bundle}
        definition={definition}
        {...(entity ? { entity } : null)}
        overrides={overrides}
        extraValidationSchema={extraValidationSchema}
        {...(renderButtons ? { renderButtons } : null)}
        submitLabel={submitLabel}
        submitAlwaysEnabled={submitAlwaysEnabled}
        onBlur={onBlur}
        onFocus={onFocus}
        {...props}
      />
    </React.Fragment>
  );
};

export default EntityForm;
